/**
 * @fileoverview The dialogs of the Sales Pipeline page.
 * @description Mounts whichever dialog the page has open — New Prospect, a prospect's details, Edit,
 * Archive or Restore, Assign Owner, Assign Target for a batch, or (mock API only) a Tenant action —
 * and turns each one's outcome into the page's flash message. The page holds only which dialog is
 * open; everything a dialog needs once it has opened is in here. The Assign Target batch is the
 * selection as it was when the person opened the dialog, so a background refetch cannot change what
 * is posted.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'

import AssignOwnerDialog from './AssignOwnerDialog'
import AssignProspectTargetModal from './AssignProspectTargetModal'
import CreateProspectForm from './CreateProspectForm'
import EditProspectDialog from './EditProspectDialog'
import ProspectArchiveDialog from './ProspectArchiveDialog'
import ProspectDetailModal from './ProspectDetailModal'
import TenantActionModal from './TenantActionModal'

import type { ProspectArchiveMode } from './ProspectArchiveDialog'
import type { TenantAction } from '@/features/sales/lib/tenant-access.utils'
import type {
  ProspectArchiveResult,
  ProspectCreated,
  ProspectEdited,
  ProspectListItem,
  ProspectOwnerAssignment,
} from '@/features/sales/types/prospect.types'
import type { AssignTargetBatchResult } from '@/features/sales/types/sales-target.types'

/** @description The dialog the page has open (there is at most one). */
export type PipelineDialog =
  | { kind: 'create' }
  | { kind: 'view'; prospectId: string }
  | { kind: 'assign-owner'; prospect: ProspectListItem }
  | { kind: 'edit'; prospect: ProspectListItem }
  | { kind: 'archive'; mode: ProspectArchiveMode; prospect: ProspectListItem }
  | { kind: 'assign-targets'; prospects: ProspectListItem[] }
  | { kind: 'tenant-action'; tenantId: string; action: TenantAction }

interface SalesPipelineDialogsProps {
  dialog: PipelineDialog | null
  onClose: () => void
  /** Shows a confirmation message on the page. */
  onFlash: (message: string) => void
  /** Clears the ticked rows once their batch has been saved. */
  onTargetsAssigned: () => void
  viewerRole: string
  viewerUserId: string
  /** Whether the viewer holds the assign capability (New Prospect offers an owner). */
  canAssignOwner: boolean
  /** Whether the mock Tenant/Target model is on, which adds the Tenant action modal. */
  legacyActionsAvailable: boolean
}

/**
 * @component
 * @param {SalesPipelineDialogsProps} props - Which dialog is open and the page's handlers.
 * @param {PipelineDialog | null} props.dialog - The open dialog, or null.
 * @param {() => void} props.onClose - Closes the open dialog.
 * @param {(message: string) => void} props.onFlash - Shows a confirmation on the page.
 * @param {() => void} props.onTargetsAssigned - Clears the row selection after a saved batch.
 * @param {string} props.viewerRole - The viewer's active role name.
 * @param {string} props.viewerUserId - The viewer's roster id (mock Tenant actions only).
 * @param {boolean} props.canAssignOwner - Whether New Prospect offers an owner.
 * @param {boolean} props.legacyActionsAvailable - Whether the mock Tenant model is on.
 */
function SalesPipelineDialogs({
  dialog,
  onClose,
  onFlash,
  onTargetsAssigned,
  viewerRole,
  viewerUserId,
  canAssignOwner,
  legacyActionsAvailable,
}: SalesPipelineDialogsProps) {
  const { t } = useTranslation()
  const refreshSalesQueries = useRefreshSalesQueries()

  /**
   * @description Confirms a new prospect: in the backlog at once, or sent for approval.
   * @param {ProspectCreated} created - The saved prospect.
   * @param {string} bankName - Its bank's name.
   */
  const onProspectCreated = (created: ProspectCreated, bankName: string) => {
    onFlash(
      t(
        created.admissionState === 'PENDING_REVIEW'
          ? 'sales.pipeline.flash.submittedForReview'
          : 'sales.pipeline.flash.created',
        { bank: bankName },
      ),
    )
  }

  /**
   * @description Confirms an owner change and closes the dialog.
   * @param {ProspectOwnerAssignment} result - The prospect's owner now.
   * @param {ProspectListItem} prospect - The prospect.
   */
  const onOwnerAssigned = (result: ProspectOwnerAssignment, prospect: ProspectListItem) => {
    onClose()
    onFlash(
      t('sales.pipeline.flash.ownerAssigned', {
        bank: prospect.bankName,
        owner: result.currentOwner?.displayName ?? '',
      }),
    )
  }

  /**
   * @description Confirms an edit (saved, or sent for review again when it was a pending
   * submission) and closes the dialog.
   * @param {ProspectEdited} result - What the edit answered.
   * @param {ProspectListItem} prospect - The prospect.
   */
  const onProspectEdited = (result: ProspectEdited, prospect: ProspectListItem) => {
    onClose()
    onFlash(
      t(
        result.admissionState === 'PENDING_REVIEW'
          ? 'sales.pipeline.flash.editedForReview'
          : 'sales.pipeline.flash.edited',
        { bank: prospect.bankName },
      ),
    )
  }

  /**
   * @description Confirms an archive or a restore and closes the dialog.
   * @param {ProspectArchiveResult} result - The prospect's archive status now.
   * @param {ProspectListItem} prospect - The prospect.
   */
  const onArchiveDone = (result: ProspectArchiveResult, prospect: ProspectListItem) => {
    onClose()
    onFlash(
      t(
        result.archiveStatus === 'ARCHIVED'
          ? 'sales.pipeline.flash.archived'
          : 'sales.pipeline.flash.restored',
        { bank: prospect.bankName },
      ),
    )
  }

  /**
   * @description Confirms a target batch, closes the dialog and clears the selection.
   * @param {AssignTargetBatchResult} result - The saved batch.
   */
  const onBatchAssigned = (result: AssignTargetBatchResult) => {
    onClose()
    onTargetsAssigned()
    onFlash(t('sales.pipeline.flash.targetsAssigned', { count: result.items.length }))
  }

  /**
   * @description Closes the Tenant action modal and refreshes what it may have changed.
   */
  const closeTenantAction = () => {
    onClose()
    refreshSalesQueries()
  }

  return (
    <>
      <CreateProspectForm
        open={dialog?.kind === 'create'}
        onClose={onClose}
        actingUserRole={viewerRole}
        canAssignOwner={canAssignOwner}
        onCreated={onProspectCreated}
      />
      {legacyActionsAvailable && (
        <TenantActionModal
          tenantId={dialog?.kind === 'tenant-action' ? dialog.tenantId : null}
          action={dialog?.kind === 'tenant-action' ? dialog.action : null}
          onClose={closeTenantAction}
          viewerUserId={viewerUserId}
          viewerRole={viewerRole}
        />
      )}
      {dialog?.kind === 'assign-targets' && (
        <AssignProspectTargetModal
          prospects={dialog.prospects}
          onClose={onClose}
          onAssigned={onBatchAssigned}
        />
      )}
      {dialog?.kind === 'edit' && (
        <EditProspectDialog
          prospect={dialog.prospect}
          onClose={onClose}
          onEdited={onProspectEdited}
        />
      )}
      {dialog?.kind === 'archive' && (
        <ProspectArchiveDialog
          mode={dialog.mode}
          prospect={dialog.prospect}
          onClose={onClose}
          onDone={onArchiveDone}
        />
      )}
      <ProspectDetailModal
        prospectId={dialog?.kind === 'view' ? dialog.prospectId : null}
        onClose={onClose}
      />
      {dialog?.kind === 'assign-owner' && (
        <AssignOwnerDialog
          prospect={dialog.prospect}
          onClose={onClose}
          onAssigned={onOwnerAssigned}
        />
      )}
    </>
  )
}

export default SalesPipelineDialogs
